import React, { useState, useMemo } from 'react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

const SAMPLE_RATES = [52, 68, 75, 82, 89, 95, 102, 108, 115, 122, 128, 135, 142, 155, 172]

const RANGE_MIN = 30
const RANGE_MAX = 200

export default function ClampVisualizer() {
  const [floor, setFloor] = useState(80)
  const [ceiling, setCeiling] = useState(150)

  const plotWidth = 100 // percentage-based

  const toPosition = (value: number): number => {
    return ((value - RANGE_MIN) / (RANGE_MAX - RANGE_MIN)) * plotWidth
  }

  const clampedRates = useMemo(
    () =>
      SAMPLE_RATES.map((rate) => ({
        original: rate,
        clamped: Math.min(Math.max(rate, floor), ceiling),
        status:
          rate < floor
            ? ('below-floor' as const)
            : rate > ceiling
              ? ('above-ceiling' as const)
              : ('within' as const),
      })),
    [floor, ceiling]
  )

  const belowCount = clampedRates.filter((r) => r.status === 'below-floor').length
  const aboveCount = clampedRates.filter((r) => r.status === 'above-ceiling').length

  const handleFloor = (val: number) => {
    const clamped = Math.max(RANGE_MIN, Math.min(val, ceiling - 1))
    setFloor(clamped)
  }

  const handleCeiling = (val: number) => {
    const clamped = Math.min(RANGE_MAX, Math.max(val, floor + 1))
    setCeiling(clamped)
  }

  return (
    <div className="space-y-5">
      {/* Slider controls */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-foreground">
            Floor (min rate)
          </label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={RANGE_MIN}
              max={ceiling - 1}
              value={floor}
              onChange={(e) => handleFloor(parseInt(e.target.value))}
              className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-rose-500"
            />
            <div className="relative w-20 shrink-0">
              <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                £
              </span>
              <Input
                type="number"
                min={RANGE_MIN}
                max={ceiling - 1}
                value={floor}
                onChange={(e) => handleFloor(parseInt(e.target.value) || RANGE_MIN)}
                className="h-8 pl-7 text-sm tabular-nums"
              />
            </div>
          </div>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-foreground">
            Ceiling (max rate)
          </label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={floor + 1}
              max={RANGE_MAX}
              value={ceiling}
              onChange={(e) => handleCeiling(parseInt(e.target.value))}
              className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-rose-500"
            />
            <div className="relative w-20 shrink-0">
              <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                £
              </span>
              <Input
                type="number"
                min={floor + 1}
                max={RANGE_MAX}
                value={ceiling}
                onChange={(e) => handleCeiling(parseInt(e.target.value) || RANGE_MAX)}
                className="h-8 pl-7 text-sm tabular-nums"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Dot plot */}
      <div className="relative rounded-lg border border-border/60 bg-white px-4 py-8">
        {/* Floor line */}
        <div
          className="absolute top-4 bottom-4 border-l-2 border-dashed border-rose-400/70 transition-all duration-300"
          style={{ left: `calc(${toPosition(floor)}% + 8px)` }}
        >
          <span className="absolute -top-1 left-1 text-[10px] font-semibold text-rose-500 whitespace-nowrap">
            Floor £{floor}
          </span>
        </div>

        {/* Ceiling line */}
        <div
          className="absolute top-4 bottom-4 border-l-2 border-dashed border-rose-400/70 transition-all duration-300"
          style={{ left: `calc(${toPosition(ceiling)}% + 8px)` }}
        >
          <span className="absolute -top-1 left-1 text-[10px] font-semibold text-rose-500 whitespace-nowrap">
            Ceiling £{ceiling}
          </span>
        </div>

        {/* Safe zone background */}
        <div
          className="absolute top-4 bottom-4 bg-teal-50/40 transition-all duration-300"
          style={{
            left: `calc(${toPosition(floor)}% + 8px)`,
            width: `${toPosition(ceiling) - toPosition(floor)}%`,
          }}
        />

        {/* Dots */}
        <div className="relative h-10">
          {clampedRates.map(({ original, clamped, status }, i) => {
            const originalPos = toPosition(original)
            const clampedPos = toPosition(clamped)
            const isAdjusted = status !== 'within'

            return (
              <React.Fragment key={i}>
                {/* Trace line from original to clamped position */}
                {isAdjusted && (
                  <div
                    className="absolute top-1/2 h-px border-t border-dashed border-rose-300/80 transition-all duration-300"
                    style={{
                      left: `calc(${Math.min(originalPos, clampedPos)}% + 8px)`,
                      width: `${Math.abs(originalPos - clampedPos)}%`,
                      transform: 'translateY(-50%)',
                    }}
                  />
                )}

                {/* Ghost dot at original position */}
                {isAdjusted && (
                  <div
                    className="absolute top-1/2 flex flex-col items-center transition-all duration-300"
                    style={{
                      left: `calc(${originalPos}% + 8px)`,
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    <div className="h-2 w-2 rounded-full border border-dashed border-rose-300 bg-rose-100/50" />
                  </div>
                )}

                {/* Actual dot at clamped position */}
                <div
                  className="absolute top-1/2 flex flex-col items-center transition-all duration-300"
                  style={{
                    left: `calc(${clampedPos}% + 8px)`,
                    transform: 'translate(-50%, -50%)',
                    zIndex: isAdjusted ? 20 : 10,
                  }}
                >
                  {/* Label */}
                  <span
                    className={cn(
                      'absolute -top-5 text-[9px] font-semibold tabular-nums whitespace-nowrap transition-colors duration-300',
                      isAdjusted ? 'text-rose-600' : 'text-teal-700'
                    )}
                  >
                    £{original}
                  </span>
                  {/* Dot */}
                  <div
                    className={cn(
                      'h-2.5 w-2.5 rounded-full shadow-sm transition-all duration-300',
                      status === 'within' && 'bg-teal-500',
                      status === 'below-floor' && 'bg-rose-500',
                      status === 'above-ceiling' && 'bg-rose-500'
                    )}
                  />
                </div>
              </React.Fragment>
            )
          })}
        </div>
      </div>

      {/* Counter */}
      <div className="flex items-center justify-center gap-4 text-xs">
        <span
          className={cn(
            'tabular-nums transition-colors duration-200',
            belowCount > 0 ? 'font-medium text-rose-600' : 'text-muted-foreground'
          )}
        >
          {belowCount} rate{belowCount !== 1 ? 's' : ''} clamped to floor
        </span>
        <span className="text-border">|</span>
        <span
          className={cn(
            'tabular-nums transition-colors duration-200',
            aboveCount > 0 ? 'font-medium text-rose-600' : 'text-muted-foreground'
          )}
        >
          {aboveCount} rate{aboveCount !== 1 ? 's' : ''} clamped to ceiling
        </span>
      </div>
    </div>
  )
}
